import React, { useState, useEffect } from 'react';
import { css } from '@emotion/css';
import { color_colors_decline, color_shades_darkest, color_variants_decline_light_1, color_colors_gold } from '../../constants/colors';
import styled from '@emotion/styled';

const alertIcon = require('../../content/images/alert-circle.svg');
const checkIcon = require('../../content/images/check-circle.svg');
const warningIcon = require('../../content/images/alert-triangle-yellow.svg');
const informationIcon = require('../../content/images/information-circle.svg');

interface PageErrorProps {
    errors?: string[]
    onClear?: () => void,
    className?: string,
    type?: 'error' | 'success' | 'warning' | 'info'
}

const PageError: React.FC<PageErrorProps> = ({ errors = [], onClear, className, type = 'error' }) => {
    const [displayErrors, setDisplayErrors] = useState<string[]>(errors);

    useEffect(() => {
        setDisplayErrors(errors);
    }, [errors]);

    const handleClick = () => {
        setDisplayErrors([]);
        if (onClear) {
            onClear();
        }
    };

    if (!displayErrors || displayErrors.length === 0) {
        return null;
    }

    const isSuccess = type === 'success';
    const isWarning = type === 'warning';
    const isInfo = type === 'info';

    const containerClass = isSuccess ? successContainerStyle : isWarning ? warningContainerStyle : isInfo ? infoContainerStyle : containerStyle;
    const icon = isSuccess ? checkIcon : isWarning ? warningIcon : isInfo ? informationIcon : alertIcon;
    const textClass = isSuccess ? 'success-text' : isWarning ? 'warning-text' : isInfo ? 'info-text' : 'error-text';

    return (
        <div onClick={handleClick}>
            <div className={`${containerClass} ${className?.length ? className : ""}`}>
                <AlertIconStyle src={icon} />
                <div className="error-text-container">
                    {displayErrors.map((e, i) => <p className={textClass} key={i}>{e?.toString() ?? ''}</p>)}
                </div>
            </div>
        </div>
    );
};
const baseToastStyle = css`
    display: flex;
    align-items: flex-start;
    position: fixed;
    right: 0;
    bottom: 32px;
    width: 360px;
    max-height: min(400px, calc(100vh - 120px));
    overflow-y: auto;
    box-sizing: border-box;
    word-break: break-word;
    padding: 16px;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
    cursor: pointer;
    z-index: 999;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);

    /* Custom thin scrollbar */
    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(0, 0, 0, 0.18);
        border-radius: 2px;
    }

    .error-text-container {
        flex: 1;
        min-width: 0;

        p {
            margin: 0 0 8px 0;
            &:last-child {
                margin-bottom: 0;
            }
        }
    }
`;

const containerStyle = css`
    ${baseToastStyle};
    background: ${color_variants_decline_light_1};
    border: 1px solid ${color_colors_decline};
    .error-text {
        color: ${color_shades_darkest};
        font-family: Muli;
        font-size: 16px;
        font-weight: bold;
        line-height: 1.5;
    } 
`

const successContainerStyle = css`
    ${baseToastStyle};
    background: #d4edda;
    border: 1px solid #28a745;
    .success-text {
        color: #155724;
        font-family: Muli;
        font-size: 16px;
        font-weight: bold;
        line-height: 1.5;
    } 
`

const warningContainerStyle = css`
    ${baseToastStyle};
    background: #fef0c7; // Solid light yellow background for better legibility
    border: 1px solid ${color_colors_gold};
    .warning-text {
        color: ${color_shades_darkest};
        font-family: Muli;
        font-size: 16px;
        font-weight: bold;
        line-height: 1.5;
    }
`

const infoContainerStyle = css`
    ${baseToastStyle};
    background: #e8f4fd;
    border: 1px solid #007dbb;
    .info-text {
        color: ${color_shades_darkest};
        font-family: Muli;
        font-size: 16px;
        font-weight: bold;
        line-height: 1.5;
    }
`

const AlertIconStyle = styled.img`
    height: 24px;
    width: 24px;
    margin-right: 8px;
    margin-top: 2px;
    flex-shrink: 0;
`;

export default PageError;
